import { RedTeamTaskIcon } from '@/components/RedTeamTaskIcon'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import { CriteriaTable, RewardBasis, criterionRows } from '@/components/CriteriaTable'
import { Icon } from '@/components/Icon'
import { AdditionalFailures, AttemptFailure, FailureChip, ScoredChip, ScoredDiagnostic } from '@/components/JobFailure'
import { PREPARING_JOB, StepMeter } from '@/components/RunFleet'
import { SidePanel } from '@/components/SidePanel'
import { useBoardData } from '@/components/useBoardData'
import { useRolloutEvents } from '@/components/runs/useRolloutEvents'
import { RolloutPlayer } from '@/components/reviews/RolloutPlayer'
import { failurePrompt } from '@/lib/failurePrompt'
import { describeFailure, attemptFailureCode, attemptFailureMessage, describeRetry, scoredNotice } from '@/lib/failures'
import { AGENT_TERMINATED, failedAttempts, gradingIdentity, jobStage } from '@/lib/runs'
import type { Attempt, EvidenceManifest, Rollout, RunConfiguration, TaskInfo } from '@/lib/runs'
import type { ImplementedRubricSpec } from '@/lib/types'
import { save, zip } from '@/lib/zip'

/** One colour vocabulary for a job's stage, shared with the lane dots in styles.css. */
const STAGE_TONE: Record<string, string> = {
  queued: 'warn', running: 'ok', completed: 'neutral', stopped: 'neutral', failed: 'err',
}

function SelectedGradeResult({ runId, jobId, attempt }: { runId: string; jobId: string; attempt: Attempt }) {
  const state = useBoardData<{ grading_key: string | null; rubric_map: ImplementedRubricSpec | null }>(
    `/eng/runs/inspect/${encodeURIComponent(runId)}/${encodeURIComponent(jobId)}/rubrics/${encodeURIComponent(attempt.attempt_id!)}`,
  )
  if (!state.data) return <>
    <p className={state.error ? 'note err' : 'muted'}>{state.error ?? 'Loading selected grading rubric…'}</p>
    {state.error && <Result attempt={attempt} rubricMap={null} />}
  </>
  if (state.data.grading_key !== attempt.promoted_regrade) return <p className="note err">The selected grade changed. Refresh this rollout to view its rubric.</p>
  return <Result attempt={attempt} rubricMap={state.data.rubric_map} />
}

function Result({ attempt, rubricMap }: { attempt: Attempt; rubricMap?: ImplementedRubricSpec | null }) {
  const summary = attempt.summary
  if (!summary) return null
  const tokens = summary.token_usage ?? {}
  // Per-criterion scoring is the answer to "why did this rollout score what it scored", so
  // the declared checks are the rows: a check the verifier never reached is its own state,
  // not a silent absence and not a failure.
  const rows = criterionRows(summary.verifier?.criteria, rubricMap)
  return <section className="panel-section">
    <div className="panel-stats">
      <div><span>Verifier score</span><strong>{summary.reward == null ? '—' : summary.reward.toFixed(2)}</strong></div>
      <div><span>Steps</span><strong>{summary.steps ?? '—'}</strong></div>
      <div><span>Duration</span><strong>{summary.duration_seconds == null ? '—' : `${Math.round(summary.duration_seconds)}s`}</strong></div>
      <div><span>Tokens</span><strong>{((tokens.input ?? 0) + (tokens.output ?? 0)).toLocaleString()}</strong></div>
    </div>
    <p className="muted small">
      Ended as <code>{summary.termination_reason ?? 'unknown'}</code>
      {summary.truncated && ' · truncated'}
      {` · ${(tokens.input ?? 0).toLocaleString()} in / ${(tokens.output ?? 0).toLocaleString()} out${tokens.reasoning ? ` / ${tokens.reasoning.toLocaleString()} reasoning` : ''}`}
    </p>
    {summary.verifier?.feedback && <p className="prewrap small">{summary.verifier.feedback}</p>}
    <ScoredDiagnostic attempt={attempt} />
    <AdditionalFailures attempt={attempt} standalone />
    <RewardBasis spec={rubricMap} />
    {rows.length > 0 && <CriteriaTable rows={rows} />}
  </section>
}

/**
 * Every attempt at this job, oldest first.
 *
 * A job is retried until it succeeds or the runner gives up, and which attempt
 * finally worked — and what the earlier ones hit — is part of reading the job.
 * The lane card only shows a count, and the Result tab only shows the failures,
 * so the sequence itself lived nowhere.
 */
export function RetryHistory({ attempts }: { attempts: Attempt[] }) {
  if (attempts.length < 2) return null
  return <section className="panel-section">
    <h3>Retry history</h3>
    <p className="muted small">{attempts.length} attempts. Recovery decisions are recorded separately for each attempt.</p>
    <ol className="retry-history">{attempts.map((attempt, index) => {
      const ok = attempt.classification === 'success'
      const stopped = attempt.classification === AGENT_TERMINATED || attempt.stopped_by_agent === true
      const failure = stopped ? undefined : describeFailure(attemptFailureCode(attempt))
      const notice = scoredNotice(attempt)
      return <li key={index} className={notice ? 'noted' : ok ? 'ok' : stopped ? 'stopped' : 'failed'}>
        <span className="retry-ordinal">{index + 1}</span>
        <span className="retry-body">
          <span className="retry-head">
            <strong>{ok ? notice?.label ?? 'Succeeded' : stopped ? 'Stopped by the authoring agent' : failure?.label ?? 'Failed'}</strong>
            {!ok && failure && <span className="failure-layer">{failure.layer}</span>}
            {describeRetry(attempt) && <span className="muted small">{describeRetry(attempt)?.label}</span>}
            {attempt.machine_id != null && <span className="muted small">Machine {attempt.machine_id} · slot {attempt.slot ?? 'unknown'}</span>}
          </span>
          {!ok && !stopped && (attemptFailureCode(attempt) || attemptFailureMessage(attempt)) &&
            <code className="muted small">{attemptFailureMessage(attempt) || attemptFailureCode(attempt)}</code>}
        </span>
        <span className="retry-score">{attempt.summary?.reward == null ? '' : attempt.summary.reward.toFixed(2)}</span>
      </li>
    })}</ol>
  </section>
}

/** Bytes at the scale a single exported state file actually reaches. */
function bytes(value: number): string {
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(0)} KB`
  return `${(value / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * The model's own exports: what it built, not what the episode looked like.
 *
 * The steps and the recording answer "what did it do"; these files answer "what did
 * it produce", which is the only form the geometry survives in once the node is
 * gone. Screenshots and video are excluded by the server, and the count of what was
 * left out is shown rather than hidden — a task whose exporter renders a view should
 * not look identical to one that writes none.
 *
 * The manifest is fetched when the section mounts, so a job with no exports says so
 * instead of offering a button that would download nothing.
 */
function ModelEvidence({ runId, rollout }: { runId: string; rollout: Rollout }) {
  const [manifest, setManifest] = useState<EvidenceManifest | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let live = true
    setManifest(null); setLoaded(false); setError('')
    api.get<EvidenceManifest>(`/eng/runs/${runId}/rollouts/${rollout.job_id}/evidence`)
      .then(value => { if (live) setManifest(value) })
      .catch(e => { if (live) setError(e instanceof ApiError ? e.message : (e as Error).message) })
      .finally(() => { if (live) setLoaded(true) })
    return () => { live = false }
  }, [runId, rollout.job_id])

  // One archive rather than one save dialog per file. Attempts are only foldered
  // when there is more than one, so the common single-attempt job unpacks flat.
  const download = useCallback(async () => {
    if (!manifest?.files.length) return
    setBusy(true)
    setError('')
    try {
      const entries = await Promise.all(manifest.files.map(async file => {
        const response = await fetch(file.url)
        if (!response.ok) throw new Error(`${file.path} could not be fetched (${response.status})`)
        const path = manifest.attempts.length > 1 ? `${file.attempt_id}/${file.path}` : file.path
        return { path, bytes: new Uint8Array(await response.arrayBuffer()) }
      }))
      save(zip(entries), `${manifest.task_id}-${manifest.job_id}-evidence.zip`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The evidence could not be downloaded.')
    } finally {
      setBusy(false)
    }
  }, [manifest])

  if (!loaded && !error) return null
  const files = manifest?.files ?? []
  return <section className="panel-section">
    <div className="panel-section-head">
      <h3>Model evidence</h3>
      {files.length > 0 && <div className="failure-actions">
        <button type="button" className="report-export" onClick={download} disabled={busy}
                title="Download the files this task's exporter wrote — no screenshots, no recording">
          <Icon name="download" size={15} />{busy ? 'Preparing…' : 'Export model evidence'}
        </button>
      </div>}
    </div>
    {error && <div className="note err small">{error}</div>}
    {files.length === 0 && !error
      ? <p className="empty">This job published no exported evidence. Either the task declares none, or its exporter did not reach finalize.</p>
      : <>
        <p className="muted small">
          {files.length} file{files.length === 1 ? '' : 's'} · {bytes(manifest?.total_bytes ?? 0)}
          {manifest && manifest.attempts.length > 1 && ` · ${manifest.attempts.length} attempts`}
          {manifest && manifest.excluded_media > 0 &&
            ` · ${manifest.excluded_media} screenshot${manifest.excluded_media === 1 ? '' : 's'} and recordings left out`}
        </p>
        <ul className="evidence-list">{files.map(file => <li key={`${file.attempt_id}/${file.path}`}>
          <a href={file.url} download={file.path.split('/').pop()}>{file.path}</a>
          <span className="muted small">{bytes(file.size)}</span>
        </li>)}</ul>
      </>}
  </section>
}

/** What the model was asked to do. Long prompts stay folded: the panel is telemetry
 *  first, and the prompt is the thing you open once to check an assumption. */
function TaskPrompt({ prompt }: { prompt: string }) {
  return <details className="panel-section task-prompt">
    <summary>Task prompt</summary>
    <p className="prewrap">{prompt}</p>
  </details>
}

/** A Notion-style peek: one job's full telemetry, over the board rather than under it. */
export function JobPanel({ runId, runName, configuration, rollout, task, taskRollouts, rubricMap, onClose, onBack, attemptId, onSelectAttempt, linkExperiment = false }: {
  runId: string; runName: string; configuration: RunConfiguration
  /** Opened outside its experiment's page (from a task or pool): link back to the experiment. */
  linkExperiment?: boolean
  rollout: Rollout; task?: TaskInfo; taskRollouts: number
  rubricMap?: ImplementedRubricSpec | null
  onClose: () => void
  onBack?: () => void
  attemptId?: string
  onSelectAttempt?: (value: string) => void
}) {
  // 'copied' holds the tick for a moment: a clipboard write has no other evidence.
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState('')
  const stage = jobStage(rollout)
  const context = `${runId}/${rollout.job_id}`
  const [chosenAttempt, setChosenAttempt] = useState<{ context: string; id: string } | null>(null)
  const selectedAttemptId = attemptId ?? (chosenAttempt?.context === context ? chosenAttempt.id : undefined) ?? [...rollout.attempts].reverse().find(a => a.classification === 'pending')?.attempt_id
    ?? rollout.attempts.find(a => a.classification === 'success')?.attempt_id ?? rollout.attempts.at(-1)?.attempt_id
  const selectedAttempt = selectedAttemptId ? rollout.attempts.find(a => a.attempt_id === selectedAttemptId) : undefined
  const grading = gradingIdentity(rollout.attempt_kind)
  const live = stage === 'running' && (!selectedAttempt || selectedAttempt.classification === 'pending')
  // A job that has run has two things worth reading and they do not belong on one
  // scroll: what it scored, and what it did. A job still going has only the second.
  const finished = (selectedAttempt && selectedAttempt.classification !== 'pending') || stage === 'completed' || stage === 'failed' || stage === 'stopped'
  const [tab, setTab] = useState<'result' | 'steps'>('result')
  // Pick the initial view per job; completion must not take an open player away.
  useEffect(() => { setTab(finished && stage !== 'stopped' ? 'result' : 'steps') }, [rollout.job_id])
  const { events, loaded, error: eventsError, refreshUrls } = useRolloutEvents(runId, rollout.job_id, live, selectedAttemptId)

  const [exporting, setExporting] = useState(false)
  /** The brief for whoever fixes this: assembled in the browser from what the panel
   *  already shows, so it needs no route and works before one is released. */
  const copyPrompt = useCallback(async () => {
    const prompt = failurePrompt({ runId, runName, configuration, rollout, taskRollouts })
    try {
      await navigator.clipboard.writeText(prompt)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not write to the clipboard.')
    }
  }, [runId, runName, configuration, rollout, taskRollouts])
  /** The report is assembled server-side from the records and the node's own log;
   *  the browser only has to save it. */
  const exportReport = useCallback(async () => {
    setExporting(true)
    try {
      const value = await api.get<{ filename: string; markdown: string }>(
        `/eng/runs/${runId}/rollouts/${rollout.job_id}/report`)
      save(new Blob([value.markdown], { type: 'text/markdown' }), value.filename)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message)
    } finally {
      setExporting(false)
    }
  }, [runId, rollout.job_id])

  const latest = events.at(-1)
  const step = Math.max(latest?.sequence ?? 0, rollout.step ?? 0)
  const failures = failedAttempts(rollout.attempts)
  const success = rollout.attempts.find(attempt => attempt.classification === 'success')
  // A failed attempt can still have burned steps, tokens and a verifier verdict
  // (verify.crashed after a full rollout); that evidence is worth the same panel.
  const result = selectedAttempt ?? success ?? [...rollout.attempts].reverse().find(attempt => attempt.summary)
  const title = task?.title || rollout.task_id

  return <SidePanel label={`job ${rollout.job_id}`} onClose={onClose} head={
    <div>
      {onBack && <button className="btn" onClick={onBack}>← Back</button>}
      <span className="eyebrow">{grading ? <span className="evaluation-label"><Icon name={grading.icon} size={16} />{grading.label}</span> : <>Job · rollout {rollout.replicate + 1} of {taskRollouts}</>}</span>
      <h2 id="job-panel-title"><RedTeamTaskIcon task={task ?? rollout} />{title}</h2>
      <div className="row job-panel-chips">
        <span className={`chip ${STAGE_TONE[stage]}`}>{stage}</span>
        {rollout.phase === 'preparing' && <span className="chip warn">preparing</span>}
        {failures.length > 0 && <FailureChip errorClass={attemptFailureCode(failures.at(-1))} compact />}
        <ScoredChip attempt={result} />
        {linkExperiment && <Link className="job-panel-experiment" to={`/eng/runs/${encodeURIComponent(runId)}`} target="_blank" rel="noreferrer"
          title={`Open experiment ${configuration.name || runId} in a new tab`}>Open experiment<Icon name="external" size={14} /></Link>}
      </div>
    </div>
  }>
    {(error || eventsError) && <div className="note err small">{error || eventsError}</div>}
    {finished && result?.history_status === 'unknown' && <p className="note small">Historical step completeness is unknown. All available steps are shown.</p>}
    {rollout.history_complete === false && <p className="note warn small">Some historical steps are unavailable. Recorded results and evidence are preserved.</p>}
    {rollout.attempts.length > 1 && <label className="panel-section">Attempt <select aria-label="Rollout attempt" value={selectedAttemptId ?? ''}
      onChange={event => { setChosenAttempt({ context, id: event.target.value }); onSelectAttempt?.(event.target.value) }}>
      {rollout.attempts.map((a, i) => <option key={a.attempt_id ?? i} value={a.attempt_id}>Attempt {i + 1} · {a.classification}</option>)}
    </select></label>}

    {rollout.waiting && <div className="note waiting"><strong>Waiting</strong><p>{rollout.waiting.text}</p>{rollout.queue_position ? <p className="muted small">Position {rollout.queue_position} in this experiment’s queue.</p> : null}</div>}
    {rollout.phase === 'preparing' && <div className="note info small">{PREPARING_JOB} The model has not been called yet, so there are no steps to show.</div>}

    {finished && <div className="panel-tabs" role="tablist" aria-label="Job view">
      {(['result', 'steps'] as const).map(name => <button type="button" key={name}
          role="tab" id={`job-tab-${name}`} aria-controls={`job-panel-${name}`} aria-selected={tab === name}
          className={tab === name ? 'selected' : ''} onClick={() => setTab(name)}>
        {name === 'result' ? 'Result' : `Steps${events.length ? ` (${events.length})` : ''}`}
      </button>)}
    </div>}

    {(!finished || tab === 'steps') && <div role={finished ? 'tabpanel' : undefined} id="job-panel-steps" aria-labelledby="job-tab-steps">
      {(events.length > 0 || rollout.video_url) ?
        <div className="job-rollout-player">
          <RolloutPlayer key={`${runId}/${rollout.job_id}/${selectedAttemptId}`} events={events} videoUrl={rollout.video_url}
                         active live={live} onStaleImage={refreshUrls} />
        </div>
        : !loaded ? <TelemetryLoading />
          : finished && <p className="empty">This job published no step telemetry. Runs from before live steps were recorded only have their result.</p>}
      {live && loaded && !latest && rollout.phase !== 'preparing' &&
        <p className="empty">Waiting for the model’s first step.</p>}
      {rollout.video_url && (!finished || events.length > 0) && <details className="panel-section rollout-details">
        <summary>Original recording</summary>
        <video controls preload="metadata" src={rollout.video_url} />
      </details>}
    </div>}

    {finished && tab === 'result' && <div role="tabpanel" id="job-panel-result" aria-labelledby="job-tab-result">
      {result ? result.promoted_regrade && result.attempt_id
        ? <SelectedGradeResult runId={runId} jobId={rollout.job_id} attempt={result} />
        : <Result attempt={result} rubricMap={rubricMap} />
        : <p className="empty">This job published no episode summary.</p>}
      <ModelEvidence runId={runId} rollout={rollout} />
      {failures.length > 0 && <section className="panel-section">
        <div className="panel-section-head">
          <h3>{failures.length === 1 ? 'Failed attempt' : `${failures.length} failed attempts`}</h3>
          <div className="failure-actions">
            <button type="button" className="report-export" onClick={exportReport} disabled={exporting}>
              <Icon name="file" size={15} />{exporting ? 'Exporting…' : 'Export failure report'}
            </button>
            <button type="button" className="report-export" onClick={copyPrompt}
                    title="Copy a brief an agent can act on: the two repositories, this job, and the error log">
              <Icon name="goofy" size={16} />{copied ? 'Copied' : 'Copy prompt'}
            </button>
          </div>
        </div>
        {failures.map((attempt, index) => <AttemptFailure key={index} attempt={attempt} ordinal={rollout.attempts.indexOf(attempt) + 1}
                                                        output={{ runId, jobId: rollout.job_id }} />)}
      </section>}
    </div>}

    <RetryHistory attempts={rollout.attempts} />

    {task?.prompt && <TaskPrompt prompt={task.prompt} />}

    <details className="job-technical">
      <summary>Job details</summary>
      <dl className="panel-facts">
        <dt>Task id</dt><dd><code>{rollout.task_id}</code></dd>
        <dt>Job id</dt><dd><code>{rollout.job_id}</code></dd>
        <dt>Seed</dt><dd>{rollout.seed}</dd>
        {rollout.max_steps ? <><dt>Step budget</dt><dd><StepMeter step={step} maxSteps={rollout.max_steps} /></dd></> : null}
        {rollout.instance_id && <><dt>Node</dt><dd><code>{rollout.instance_id}</code></dd></>}
        {rollout.heartbeat?.attempt_id && <><dt>Attempt</dt><dd><code>{rollout.heartbeat.attempt_id}</code></dd></>}
        {rollout.heartbeat?.elapsed_seconds != null && <><dt>Running for</dt><dd>{Math.round(rollout.heartbeat.elapsed_seconds)}s</dd></>}
      </dl>
    </details>
  </SidePanel>
}

/** The steps are being fetched. A determinate bar would be a lie — the page does not
 *  know how many steps there are until they arrive — so this is a plain indeterminate
 *  one, which is still the honest answer to "is anything happening?". */
export function TelemetryLoading({ label = 'Loading telemetry…' }: { label?: string }) {
  return <div className="panel-loading" role="status">
    <span className="panel-loading-bar" aria-hidden="true"><i /></span>
    <span className="muted small">{label}</span>
  </div>
}
